import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useMarket } from "../components/Layout";
import { fmtCurrency, marketPill, kindLabel, scoreChipClass } from "../lib/format";
import { toast, Toaster } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";

const COLS = [
  { key: "low_conf", label: "Low Confidence", filter: (t) => t.confidence < 0.85 && t.kind !== "unclear" },
  { key: "unclear", label: "Unclear Classification", filter: (t) => t.kind === "unclear" },
  { key: "borderline", label: "Borderline Score (50-70)", filter: (t) => t.score >= 0.5 && t.score < 0.7 && t.confidence >= 0.85 && t.kind !== "unclear" },
];

export default function ReviewQueue() {
  const market = useMarket();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkConf, setBulkConf] = useState("0.95");
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(null); // { state, matches, mode: 'selected'|'threshold', minConf? }
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/review-queue", { params: { market } });
      setItems(data);
      // drop selections that no longer exist in queue
      setSelected(prev => new Set([...prev].filter(id => data.some(t => t.id === id))));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [market]);

  const decide = async (tx, state) => {
    try {
      await client.post(`/review-queue/${tx.id}`, { state, note: state });
      toast.success(`${state.toUpperCase()} · ${tx.ticker}`);
      setSelected(prev => { const n = new Set(prev); n.delete(tx.id); return n; });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const selectAllVisible = () => setSelected(new Set(items.map(t => t.id)));
  const selectByConf = () => {
    const minConf = parseFloat(bulkConf);
    setSelected(new Set(items.filter(t => t.confidence >= minConf).map(t => t.id)));
  };

  const matchingByConf = useMemo(
    () => items.filter(t => t.confidence >= parseFloat(bulkConf)),
    [items, bulkConf],
  );

  const openBulk = (state) => {
    if (selected.size > 0) {
      const matches = items.filter(t => selected.has(t.id));
      setConfirmOpen({ state, matches, mode: "selected" });
    } else {
      setConfirmOpen({ state, matches: matchingByConf, mode: "threshold", minConf: parseFloat(bulkConf) });
    }
  };

  const executeBulk = async () => {
    if (!confirmOpen) return;
    try {
      const payload = confirmOpen.mode === "selected"
        ? { state: confirmOpen.state, tx_ids: confirmOpen.matches.map(m => m.id) }
        : { state: confirmOpen.state, min_confidence: confirmOpen.minConf, min_score: 0,
            market: market !== "ALL" ? market : null, kind_group: "all" };
      const { data } = await client.post("/review-queue/bulk", payload);
      const batchId = data.batch_id;
      const state = confirmOpen.state;
      const count = data.updated;
      setConfirmOpen(null);
      clearSel();
      load();

      // Undo toast — 8s window
      if (batchId && count > 0) {
        toast(`Bulk ${state.toUpperCase()} · ${count} items updated`, {
          duration: 8000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const r = await client.post(`/review-queue/undo/${batchId}`);
                toast.success(`Undone · restored ${r.data.restored} items`);
                load();
              } catch (e) {
                toast.error("Undo failed: " + (e?.response?.data?.detail || e.message));
              }
            },
          },
        });
      } else {
        toast.success(`Bulk ${state.toUpperCase()} · ${count} items updated`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    }
  };

  return (
    <div data-testid="review-page">
      <Toaster theme="dark" position="top-right" />
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-[18px] font-semibold tracking-tight">Review Queue</h2>
          <span className="mono text-[12px] text-[color:var(--text-tertiary)]">{items.length} pending{selected.size > 0 && ` · ${selected.size} selected`}</span>
        </div>
        <div className="flex items-center gap-2 glass px-3 py-2 rounded-full flex-wrap" data-testid="bulk-toolbar">
          <span className="mono text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Bulk conf ≥</span>
          <Select value={bulkConf} onValueChange={setBulkConf}>
            <SelectTrigger data-testid="bulk-conf-selector" className="mono w-20 h-7 text-[11px] bg-black/25 border-white/[0.08] rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="glass rounded-2xl border-white/[0.06]">
              {["0.80","0.90","0.95","0.99"].map(v => (
                <SelectItem key={v} value={v} className="mono text-[11px] rounded-lg">{Math.round(parseFloat(v)*100)}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={selectByConf} data-testid="select-by-conf" className="mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full text-[color:var(--cyan)] border border-white/[0.10] hover:border-[color:var(--cyan)]/50">Tick matching</button>
          <button onClick={selectAllVisible} data-testid="select-all" className="mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full text-[color:var(--text-secondary)] border border-white/[0.10] hover:border-white/[0.24]">Tick all</button>
          {selected.size > 0 && <button onClick={clearSel} data-testid="clear-selection" className="mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full text-[color:var(--pink)] border border-white/[0.10] hover:border-[color:var(--pink)]/50">Clear</button>}
          <div className="w-px h-6 bg-white/[0.08]" />
          <button
            onClick={() => openBulk("approved")}
            data-testid="bulk-approve-btn"
            className="mono text-[10px] px-3 py-1.5 rounded-full font-semibold border transition-colors"
            style={{ borderColor: "rgba(94,234,212,0.4)", color: "var(--score-high)" }}
          >Approve {selected.size > 0 ? `selected (${selected.size})` : "matching"}</button>
          <button
            onClick={() => openBulk("rejected")}
            data-testid="bulk-reject-btn"
            className="mono text-[10px] px-3 py-1.5 rounded-full font-semibold border transition-colors"
            style={{ borderColor: "rgba(243,143,178,0.4)", color: "var(--pink)" }}
          >Reject {selected.size > 0 ? `selected (${selected.size})` : "matching"}</button>
        </div>
      </div>

      {loading && <div className="mono text-[color:var(--text-tertiary)] terminal-loader">Loading queue</div>}

      <div className="grid grid-cols-3 gap-4" data-testid="kanban">
        {COLS.map((col) => {
          const rows = items.filter(col.filter);
          return (
            <div key={col.key} className="glass overflow-hidden min-h-[500px]" data-testid={`kanban-col-${col.key}`}>
              <div className="data-header flex items-center justify-between">
                <span>{col.label}</span>
                <span className="mono text-[color:var(--lemon)] font-semibold">{rows.length}</span>
              </div>
              <div className="p-3 space-y-3">
                {rows.map((tx) => {
                  const isSel = selected.has(tx.id);
                  return (
                    <div key={tx.id} className={`rounded-xl border p-3 transition-colors ${isSel ? "border-[color:var(--cyan)]/60 bg-[color:var(--cyan)]/[0.04]" : "border-white/[0.06] bg-black/25 hover:border-[color:var(--cyan)]/40"}`} data-testid={`queue-card-${tx.ticker}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={isSel}
                            onCheckedChange={() => toggle(tx.id)}
                            data-testid={`check-${tx.ticker}`}
                            className="rounded-md data-[state=checked]:bg-[color:var(--cyan)] data-[state=checked]:border-[color:var(--cyan)] border-white/30"
                          />
                          <span className={marketPill(tx.market)}>{tx.market}</span>
                          <span className="mono font-semibold text-[color:var(--cyan)] cursor-pointer" onClick={() => nav(`/tx/${tx.id}`)}>{tx.ticker}</span>
                        </div>
                        <span className={scoreChipClass(tx.score)}>{(tx.score * 100).toFixed(0)}</span>
                      </div>
                      <div className="text-[color:var(--text-primary)] text-[13px] font-medium">{tx.insider_name}</div>
                      <div className="text-[11px] text-[color:var(--text-tertiary)] mb-2">{tx.insider_role}</div>
                      <div className="mono text-[11px] flex items-center justify-between mb-1">
                        <span className="text-[color:var(--text-secondary)]">{kindLabel(tx.kind)}</span>
                        <span className="font-medium">{fmtCurrency(tx.value, tx.currency)}</span>
                      </div>
                      <div className="mono text-[10px] text-[color:var(--text-tertiary)] mb-3">conf {(tx.confidence * 100).toFixed(0)}% · {tx.extraction_method}</div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button data-testid={`approve-${tx.ticker}`} onClick={() => decide(tx, "approved")} className="text-[10px] uppercase tracking-wider py-1.5 rounded-full font-semibold border transition-colors" style={{ borderColor: "rgba(94,234,212,0.4)", color: "var(--score-high)" }}>Approve</button>
                        <button data-testid={`reject-${tx.ticker}`} onClick={() => decide(tx, "rejected")} className="text-[10px] uppercase tracking-wider py-1.5 rounded-full font-semibold border transition-colors" style={{ borderColor: "rgba(243,143,178,0.4)", color: "var(--pink)" }}>Reject</button>
                        <button data-testid={`correct-${tx.ticker}`} onClick={() => decide(tx, "corrected")} className="text-[10px] uppercase tracking-wider py-1.5 rounded-full font-semibold border transition-colors" style={{ borderColor: "rgba(245,223,124,0.4)", color: "var(--lemon)" }}>Correct</button>
                      </div>
                    </div>
                  );
                })}
                {rows.length === 0 && !loading && <div className="text-[12px] text-[color:var(--text-tertiary)] mono p-3">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirmOpen} onOpenChange={(v) => { if (!v) setConfirmOpen(null); }}>
        <DialogContent className="glass rounded-3xl max-w-lg border-white/[0.06]" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-semibold">Confirm bulk {confirmOpen?.state}</DialogTitle>
            <DialogDescription className="text-[color:var(--text-tertiary)]">
              {confirmOpen?.mode === "selected"
                ? "You are about to update the items you have ticked."
                : `You are about to update every pending item with confidence ≥ ${Math.round((confirmOpen?.minConf ?? 0) * 100)}%.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="mono text-[13px] mb-3">
              <span className="text-[color:var(--text-secondary)]">Will {confirmOpen?.state}</span>{" "}
              <span className="grad-text text-[26px] font-semibold">{confirmOpen?.matches?.length ?? 0}</span>{" "}
              <span className="text-[color:var(--text-secondary)]">{confirmOpen?.mode === "selected" ? "ticked" : "matching"} items</span>
            </div>
            <div className="max-h-64 overflow-auto rounded-xl bg-black/25 border border-white/[0.06] divide-y divide-white/[0.04]" data-testid="bulk-preview-list">
              {(confirmOpen?.matches ?? []).slice(0, 30).map(t => (
                <div key={t.id} className="grid grid-cols-[70px_90px_1fr_60px] items-center px-3 py-2 text-[12px]">
                  <div><span className={marketPill(t.market)}>{t.market}</span></div>
                  <div className="mono font-semibold text-[color:var(--cyan)]">{t.ticker}</div>
                  <div className="text-[color:var(--text-secondary)] truncate">{t.insider_name}</div>
                  <div className="mono text-right text-[color:var(--text-tertiary)]">{Math.round(t.confidence * 100)}%</div>
                </div>
              ))}
              {confirmOpen?.matches?.length === 0 && <div className="p-4 text-center text-[color:var(--text-tertiary)] mono text-[12px]">Nothing matches — tick some cards or lower the confidence threshold.</div>}
              {confirmOpen?.matches?.length > 30 && <div className="p-3 text-center text-[color:var(--text-tertiary)] mono text-[11px]">…and {confirmOpen.matches.length - 30} more</div>}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setConfirmOpen(null)} className="btn-ghost" data-testid="bulk-cancel">Cancel</button>
            <button
              onClick={executeBulk}
              disabled={!confirmOpen?.matches?.length}
              data-testid="bulk-confirm"
              className="btn-primary"
              style={confirmOpen?.state === "rejected" ? { background: "var(--pink)" } : {}}
            >
              {confirmOpen?.state === "approved" ? "Approve all" : "Reject all"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
